<template>
    <div class="border rounded-lg"
         :class="{'bg-light-module border-light-border': !darkMode, 'bg-dark-module border-dark-border': darkMode}">
        <div class="p-5 flex items-center justify-between">
            <h5 class="text-primary-500 text-sm uppercase font-semibold leading-tight">Support Officers List</h5>
            <button @click="openModal"
                    class="transition duration-200 bg-primary-500 hover:bg-blue-500 text-white text-sm font-medium focus:outline-none py-2 rounded-md px-5">
                Add Support Officer
            </button>
        </div>
        <div class="grid grid-cols-5 gap-x-3 mb-2 px-5">
            <p class="text-slate font-medium-400 uppercase text-xs col-span-2">User</p>
            <p class="text-slate font-medium-400 uppercase text-xs col-span-2">ST Round Robin</p>
            <p class="text-slate font-medium-400 uppercase text-xs"></p>
        </div>
        <div class="border-t border-b h-64 overflow-y-auto"
             :class="{'bg-light-background  border-light-border': !darkMode, 'bg-dark-background border-dark-border': darkMode}">
            <div class="h-full">
                <div class="flex items-center h-full justify-center" v-if="loading">
                    <loading-spinner></loading-spinner>
                </div>
                <div class="grid grid-cols-5 gap-x-3 border-b px-5 py-3"
                     v-else
                     v-for="supportOfficer in supportOfficers" :key="supportOfficer.id"
                     :class="{'text-slate-900 hover:bg-light-module border-light-border': !darkMode, 'text-slate-100 hover:bg-dark-module border-dark-border': darkMode}">
                    <p class="text-sm col-span-2 truncate">
                        {{ supportOfficer.name }}
                    </p>
                    <p class="text-sm col-span-2 pl-1 truncate">
                        {{ supportOfficer.include_in_round_robin ? "Yes" : "No" }}
                    </p>
                    <ActionsHandle :dark-mode="darkMode" @edit="editSupportOfficer(supportOfficer)" @delete="deleteSupportOfficer(supportOfficer)"/>
                </div>
            </div>
        </div>
        <div class="p-3"></div>
        <Modal :dark-mode="darkMode" :small="true" @close="closeModal" close-text="Cancel"
               @confirm="saveSupportOfficer"
               :confirm-text="confirmText" v-if="showModal">
            <template v-slot:header>
                <h4 class="text-xl font-medium">{{ supportOfficer.id ? `Edit ${supportOfficer.name}` : 'Add Support Officer' }}</h4>
            </template>
            <template v-slot:content>
                <div class="px-6">
                    <div class="mb-6" v-if="!supportOfficer.id">
                        <label class="block pb-2 font-medium">User</label>
                        <Autocomplete
                            :input-class="['w-full border border-grey-350 rounded px-4 py-3', {'border-grey-350 bg-light-module': !darkMode, 'border-blue-400 bg-dark-background text-blue-400': darkMode}]"
                            :list-class="['absolute top-0 w-full border rounded', {'border-grey-200 bg-light-module': !darkMode, 'border-dark-40 bg-dark-module': darkMode}]"
                            :option-class="['cursor-pointer px-5 py-2 my-1', {'hover:bg-grey-100': !darkMode, 'hover:bg-blue-800': darkMode}]"
                            v-model="supportOfficer.user_id"
                            :value="supportOfficer.user_id"
                            :options="userOptions"
                            @search="searchUsers"
                            :placeholder="'Enter Name'">
                        </Autocomplete>
                    </div>
                    <div class="mb-5 grid grid-cols-2 gap-2">
                        <div>
                            <p class="block pb-2 font-medium">Include in sales tool round robin?</p>
                            <Dropdown :dark-mode="darkMode" :options="roundRobinTypes" v-model="supportOfficer.include_in_round_robin"
                                      :selected="supportOfficer.include_in_round_robin"/>
                        </div>
                    </div>
                </div>
                <div v-if="error !== null" class="px-6 my-4 font-bold text-red-400">{{ error }}</div>
            </template>
        </Modal>
    </div>
</template>

<script>
import Modal from "../../Shared/components/Modal";
import Dropdown from "../../Shared/components/Dropdown";
import ActionsHandle from "../../Shared/components/ActionsHandle";
import Autocomplete from "../../Shared/components/Autocomplete";
import LoadingSpinner from "../../Shared/components/LoadingSpinner";

export default {
    name: "SupportOfficersList",
    components: {Modal, Dropdown, ActionsHandle, Autocomplete, LoadingSpinner},
    props: ["api", "darkMode"],
    data() {
        return {
            supportOfficers: [],
            loading: false,
            supportOfficer: {
                user_id: null,
                include_in_round_robin: 0
            },
            showModal: false,
            roundRobinTypes: [
                {id: 0, name: "No"},
                {id: 1, name: "Yes"}
            ],
            error: null,
            userOptions: [],
            saving: false
        }
    },
    created() {
        this.getSupportOfficers();
    },
    computed: {
        confirmText: function () {
            if (this.saving) return 'Saving...';
            if (this.supportOfficer.id) return 'Update';

            return 'Save';
        }
    },
    methods: {
        getSupportOfficers() {
            this.loading = true;
            this.api.getSupportOfficers()
                .then(resp => this.supportOfficers = resp.data.data.support_officers)
                .finally(() => this.loading = false);
        },
        saveSupportOfficer() {
            this.saving = true;
            this.resetError();

            if (this.supportOfficer.id) {
                this.updateSupportOfficer();
                return;
            }

            this.createSupportOfficer();
        },
        createSupportOfficer() {
            this.api.createSupportOfficer(this.supportOfficer)
                .then(() => {
                    this.getSupportOfficers();
                    this.closeModal();
                })
                .catch(e => this.error = e.response.data.message)
                .finally(() => this.saving = false);
        },
        editSupportOfficer(supportOfficer) {
            this.supportOfficer = {...supportOfficer};
            this.openModal();
        },
        updateSupportOfficer() {
            this.api.updateSupportOfficer(this.supportOfficer.id, this.supportOfficer)
                .then(() => {
                    this.getSupportOfficers();
                    this.closeModal();
                })
                .catch(e => this.error = e.response.data.message)
                .finally(() => this.saving = false);
        },
        deleteSupportOfficer(supportOfficer) {
            this.api.deleteSupportOfficer(supportOfficer.id).then(() => this.getSupportOfficers());
        },
        openModal() {
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
            this.supportOfficer = {
                user_id: null,
                include_in_round_robin: 0
            };
            this.resetError();
        },
        searchUsers(query) {
            this.api.searchUserNames(query).then(resp => this.userOptions = resp.data.data.users);
        },
        resetError() {
            this.error = null;
        }
    }
}
</script>
